import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetAPICredentials } from '@/api/APICredentials/useGetAPICredentials';
import { Button } from '@/component-library/primitives/Button/Button';
import APICredentialsLoader from '@/component-library/primitives/Loaders/APICredentialsLoader';
import { ProvideToasts, Toast, ToastType } from '@/component-library/primitives/Toast/Toast';
import NoDataBoard from '@/component-library/widgets/NoDataBoard/NoDataBoard';
import Table from '@/component-library/widgets/Table/Table';
import { HttpMethodsEnum, PermissionsEnum } from '@/modules/shared/enums/enums';
import usePermissionChecker from '@/modules/shared/hooks/usePermissionChecker';

import CreateAPICredentialModal from './CreateAPICredentialModal';
import { useAPICredentialsTable } from './useAPICredentialsTable';

const APICredentials: React.FC = () => {
  const { t } = useTranslation();

  const { data, isLoading, refetch } = useGetAPICredentials();
  const { columns } = useAPICredentialsTable();

  const isActionAllowed = usePermissionChecker(PermissionsEnum.API_CREDENTIALS, HttpMethodsEnum.POST);

  const [open, setOpen] = useState<boolean>(false);
  const [toastInfo, setToastInfo] = useState<{ message: string; toastType: string }>({ message: '', toastType: '' });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const handleOpenModal = React.useCallback(() => {
    if (!isActionAllowed) {
      setToastInfo({ message: t('CommonErrors.actionNotAllowed'), toastType: 'error' });
      setOpen(true);
      return;
    }
    setIsModalOpen(true);
  }, [isActionAllowed, t]);

  const handleModalSuccess = React.useCallback(() => {
    void refetch();
  }, [refetch]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-row items-center mb-4">
        <div className="ml-auto">
          <Button className="!w-[10.5rem]" onClick={handleOpenModal}>
            {t('APICredentialsPage.buttonNewAPICredentials')}
          </Button>
        </div>
      </div>
      {isLoading ? (
        <APICredentialsLoader />
      ) : data && data.length > 0 ? (
        <Table columns={columns} data={data ?? []} />
      ) : (
        <NoDataBoard
          description={t('NoDataBoard.addYourFirstAPICredentials')}
          actionButton={
            <Button className="!w-[10.5rem] mt-4" onClick={handleOpenModal}>
              {t('APICredentialsPage.buttonNewAPICredentials')}
            </Button>
          }
        />
      )}
      <CreateAPICredentialModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={handleModalSuccess}
      />
      <ProvideToasts>
        <Toast
          isOpen={open}
          setOpen={setOpen}
          message={toastInfo.message}
          toastType={toastInfo.toastType as ToastType}
        />
      </ProvideToasts>
    </div>
  );
};

export default APICredentials;
